import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { ProjectMember, Project } from '../../../types/domain';
import { Save } from 'lucide-react';

interface ManageMemberProjectsModalProps {
  managingMember: ProjectMember | null;
  onClose: () => void;
  projects: Project[];
  assignedProjects: string[];
  onAssignedProjectsChange: (pids: string[]) => void;
  isLoading: boolean;
  isSaving: boolean;
  onSave: () => void;
}

export const ManageMemberProjectsModal: React.FC<ManageMemberProjectsModalProps> = ({
  managingMember,
  onClose,
  projects,
  assignedProjects,
  onAssignedProjectsChange,
  isLoading,
  isSaving,
  onSave,
}) => {
  if (!managingMember) return null;

  const isTargetAdmin =
    (managingMember.role || '').toLowerCase() === 'admin' ||
    (managingMember.role || '').toLowerCase() === 'owner';

  return (
    <Modal
      isOpen={Boolean(managingMember)}
      onClose={onClose}
      title="Member Settings"
    >
      <div className="space-y-4">
        {/* Member Identity Card */}
        <div className="flex items-center justify-between p-3 rounded-lg border border-cf-border bg-cf-surface">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-cf-orange/15 text-cf-orange flex items-center justify-center font-bold text-xs shrink-0">
              {(managingMember.full_name || managingMember.email || 'U')[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-cf-textPrimary text-xs truncate">
                {managingMember.full_name || 'Organization Member'}
              </div>
              <div className="text-cf-textMuted text-xs truncate">
                {managingMember.email}
              </div>
            </div>
          </div>
          <span className="text-xs px-2 py-0.5 rounded font-medium capitalize border bg-cf-bg border-cf-border text-cf-textSecondary">
            {managingMember.role || 'Member'}
          </span>
        </div>

        {/* Informational or Interactive Projects Selection */}
        {isTargetAdmin ? (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-cf-textPrimary">
                Full Organization Access
              </h4>
              <p className="text-xs text-cf-textSecondary mt-0.5">
                Organization administrators and owners have full access to all projects and datastores.
              </p>
            </div>
            <div className="p-3 rounded-lg border border-cf-border bg-cf-surface text-xs text-cf-textSecondary">
              Individual project assignments are only applicable to standard members. This user automatically has full management privileges across all {projects.length} projects in this organization.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <h4 className="text-sm font-semibold text-cf-textPrimary">
                Project Access
              </h4>
              <p className="text-xs text-cf-textSecondary mt-0.5">
                Projects this member is authorized to manage.
              </p>
            </div>

            {/* Counter and select-all */}
            <div className="flex items-center justify-between py-0.5 text-xs">
              <span className="text-xs text-cf-textSecondary font-medium">
                <span className="text-cf-textPrimary font-semibold">{assignedProjects.length}</span> / {projects.length}
              </span>
              {projects.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    if (assignedProjects.length === projects.length) {
                      onAssignedProjectsChange([]);
                    } else {
                      onAssignedProjectsChange(projects.map((p) => p.id));
                    }
                  }}
                  className="text-cf-orange"
                >
                  {assignedProjects.length === projects.length ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="py-8 text-center text-xs text-cf-textMuted">
                Loading member assignments...
              </div>
            ) : projects.length === 0 ? (
              <div className="py-6 text-center text-xs text-cf-textMuted border border-dashed border-cf-border rounded-lg">
                No projects created in this organization yet.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {projects.map((p) => {
                  const isChecked = assignedProjects.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-colors ${
                        isChecked
                          ? 'border-cf-orange/50 bg-cf-orange/5'
                          : 'border-cf-border hover:bg-cf-surface'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onAssignedProjectsChange([...assignedProjects, p.id]);
                            } else {
                              onAssignedProjectsChange(assignedProjects.filter((id) => id !== p.id));
                            }
                          }}
                          size="sm"
                        />
                        <span className="font-medium text-cf-textPrimary truncate">
                          {p.name}
                        </span>
                      </div>
                      <span className="text-xs text-cf-textMuted shrink-0">
                        {p.table_count ?? 0} {p.table_count === 1 ? 'datastore' : 'datastores'}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Action Footer */}
        <div className="flex justify-end gap-2 pt-3 border-t border-cf-border">
          <Button
            type="button"
            variant="secondary"
            size="xs"
            onClick={onClose}
          >
            {isTargetAdmin ? 'Close' : 'Cancel'}
          </Button>
          {!isTargetAdmin && (
            <Button
              type="button"
              variant="primary"
              size="xs"
              disabled={isSaving || isLoading}
              isLoading={isSaving}
              onClick={onSave}
              leftIcon={<Save size={13} />}
            >
              Save Assignments
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
